import { format } from 'date-fns';

import {
  DatetimeMonthFormat,
  FriendlyAdditions,
  FriendlyFormatName,
  TypeOfDate,
} from './types';

import { DATE_FNS_LOCALE } from 'app/constants/dateFNS';
import type { SupportedLang, tFunction } from 'app/lang';
import { getFriendlyDateFormats } from 'app/lib/datetime/constants';

import { formatByLang, isCurrentYear } from './utils';

export const friendlyFormatByLang = (
  date: Date,
  lang: SupportedLang,
  formatName: FriendlyFormatName,
  additions: FriendlyAdditions,
  t: tFunction,
): string => {
  return format(date, getFriendlyDateFormats(t, lang)[formatName][additions], {
    locale: DATE_FNS_LOCALE[lang],
  });
};

export const getFriendlyFormatValue = (
  date: Date,
  lang: SupportedLang,
  options?: {
    friendlyFormat?: FriendlyAdditions;
    typeOfDate?: TypeOfDate;
    monthFormat?: DatetimeMonthFormat;
    withoutYear?: boolean;
  },
  t?: tFunction,
): string => {
  if (!t) {
    return formatByLang(date, lang, (isCurrentYear(date) || options?.withoutYear) ? 'long' : 'longWithYear', options?.monthFormat);
  }

  const friendlyFormat = options?.friendlyFormat ?? FriendlyAdditions.WITH_TIME;
  const typeOfDate = options?.typeOfDate ?? TypeOfDate.HUMAN_DATE;

  switch (friendlyFormat) {
    case FriendlyAdditions.DEFAULT: {
      if (isYesterday(date)) {
        return getFriendlyDateFormats(t, lang).yesterday.default;
      }

      if (isToday(date)) {
        return getFriendlyDateFormats(t, lang).today.default;
      }

      if (isTomorrow(date)) {
        return getFriendlyDateFormats(t, lang).tomorrow.default;
      }

      return formatByLang(date, lang, (isCurrentYear(date) || options?.withoutYear) ? 'short' : 'shortWithYear', options?.monthFormat);
    }

    case FriendlyAdditions.WITH_TIME: {
      if (isYesterday(date, typeOfDate)) {
        return friendlyFormatByLang(
          date,
          lang,
          FriendlyFormatName.YESTERDAY,
          FriendlyAdditions.WITH_TIME,
          t,
        );
      }

      if (isToday(date, typeOfDate)) {
        return friendlyFormatByLang(
          date,
          lang,
          FriendlyFormatName.TODAY,
          FriendlyAdditions.WITH_TIME,
          t,
        );
      }

      if (isTomorrow(date, typeOfDate)) {
        return friendlyFormatByLang(
          date,
          lang,
          FriendlyFormatName.TOMORROW,
          FriendlyAdditions.WITH_TIME,
          t,
        );
      }

      break;
    }
  }

  return formatByLang(date, lang, (isCurrentYear(date) || options?.withoutYear) ? 'long' : 'longWithYear', options?.monthFormat);
};

function isYesterday(date: Date, typeOfDate?: TypeOfDate): boolean {
  const yesterday = createDefaultDate(typeOfDate);
  yesterday.setDate(yesterday.getDate() - 1);

  const today = createDefaultDate(typeOfDate);

  return isBetween(date, yesterday, today);
}

function isToday(date: Date, typeOfDate?: TypeOfDate): boolean {
  const today = createDefaultDate(typeOfDate);

  const tomorrow = createDefaultDate(typeOfDate);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return isBetween(date, today, tomorrow);
}

function isTomorrow(date: Date, typeOfDate?: TypeOfDate): boolean {
  const tomorrow = createDefaultDate(typeOfDate);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayAfterTomorrow = createDefaultDate(typeOfDate);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

  return isBetween(date, tomorrow, dayAfterTomorrow);
}

function createDefaultDate(typeOfDate: TypeOfDate = TypeOfDate.HUMAN_DATE): Date {
  const date = new Date();

  typeOfDate === TypeOfDate.HUMAN_DATE
    ? date.setHours(0, 0, 0, 0)
    : date.setHours(6, 0, 0, 0);

  return date;
}

function isBetween(date: Date, leftDate: Date, rightDate: Date): boolean {
  return date >= leftDate && date < rightDate;
}
